import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, AlertTriangle, Trash2, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { formatDate, isProcessing } from "../lib/constants";
import { StatusBadge } from "../components/StatusBadge";
import { CategoryBadge } from "../components/CategoryBadge";
import { PipelineStepper } from "../components/PipelineStepper";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { KnowledgeEditDialog } from "../components/KnowledgeEditDialog";

export default function ContentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("sorgente");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(null);
  const [intents, setIntents] = useState([]);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/contents/${id}`);
      setC(data);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [id]);

  useEffect(() => {
    load();
    api.get("/kb/meta").then(({ data }) => setIntents(data.intents)).catch(() => {});
  }, [load]);

  useEffect(() => {
    if (!c || !isProcessing(c.stato)) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [c, load]);

  const retry = async () => {
    setRetrying(true);
    try {
      await api.post(`/contents/${id}/retry`);
      toast.success("Elaborazione riavviata");
      await load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setRetrying(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/contents/${id}`);
      toast.success("Contenuto eliminato");
      navigate("/contenuti");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  if (!c) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 spin-slow" /> Caricamento…</div>
    );
  }

  const sourceText = c.tipo === "testo" ? c.source_text : c.transcript;
  const stepLabel = c.tipo === "testo" ? "Testo sorgente" : c.file_kind === "pdf" ? "Testo estratto dal PDF" : c.tipo === "link" ? "Descrizione + trascrizione" : "Trascrizione";

  return (
    <div data-testid="content-detail-page" className="space-y-8">
      <Link to="/contenuti" data-testid="back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white">
        <ArrowLeft className="h-4 w-4" /> Tutti i contenuti
      </Link>

      <div className="fade-up flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            {c.tipo === "testo" ? "Testo" : c.tipo === "link" ? `Link · ${c.platform}` : `File · ${c.file_kind}`} · {formatDate(c.created_at)}
          </p>
          <h1 data-testid="content-title" className="mt-2 break-words text-3xl font-bold tracking-tight sm:text-4xl">{c.titolo}</h1>
          {c.source_url && (
            <a data-testid="content-source-url" href={c.source_url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block max-w-full truncate text-xs font-mono text-primary hover:underline">
              {c.source_url}
            </a>
          )}
          {c.original_filename && <p className="mt-1 text-xs font-mono text-muted-foreground">{c.original_filename} · {(c.size / 1048576).toFixed(2)} MB{c.detected_language ? ` · lingua: ${c.detected_language}` : ""}</p>}
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge stato={c.stato} />
          <button data-testid="content-delete-btn" onClick={() => setConfirmDelete(true)} className="rounded-full border border-border p-2.5 text-muted-foreground transition-colors hover:border-red-500/50 hover:text-red-400" title="Elimina contenuto">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <section className="fade-up surface p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <PipelineStepper stato={c.stato} tipo={c.tipo} />
          {(c.stato === "ERRORE" || c.stato === "INDICIZZATO" || c.stato === "NON_PERTINENTE") && (
            <button
              data-testid="retry-btn"
              onClick={retry}
              disabled={retrying}
              className="inline-flex items-center gap-2 self-start rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:border-primary hover:text-white disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${retrying ? "spin-slow" : ""}`} /> {c.stato === "ERRORE" ? "Riprova" : c.stato === "NON_PERTINENTE" ? "Rianalizza" : "Rielabora"}
            </button>
          )}
        </div>
        {c.job && c.job.attempts > 0 && c.stato !== "INDICIZZATO" && (
          <p data-testid="job-attempts" className="mt-3 text-xs font-mono text-muted-foreground">
            Tentativi: {c.job.attempts}/{c.job.max_attempts} · stato job: {c.job.status}
          </p>
        )}
      </section>

      {c.error_message && (
        <div data-testid="error-banner" className={`fade-up flex items-start gap-3 rounded-xl border p-4 text-sm ${c.stato === "ERRORE" ? "border-red-500/30 bg-red-500/10" : "border-amber-500/30 bg-amber-500/10"}`}>
          <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${c.stato === "ERRORE" ? "text-red-400" : "text-amber-400"}`} />
          <div>
            <p className={`font-semibold ${c.stato === "ERRORE" ? "text-red-300" : "text-amber-300"}`}>
              {c.stato === "ERRORE" ? "Elaborazione fallita" : c.stato === "NON_PERTINENTE" ? "Contenuto non pertinente alla vendita: non aggiunto alla knowledge base" : "Tentativo fallito, nuovo tentativo in corso"}
            </p>
            <p data-testid="error-message" className="mt-1 break-words text-muted-foreground">{c.error_message}</p>
          </div>
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-2">
        <section className="fade-up min-w-0">
          <div className="inline-flex rounded-full bg-secondary p-1">
            <button data-testid="tab-sorgente" onClick={() => setTab("sorgente")} className={`rounded-full px-4 py-1.5 text-xs font-medium transition-all ${tab === "sorgente" ? "bg-card text-white shadow" : "text-muted-foreground"}`}>{stepLabel}</button>
            <button data-testid="tab-meta" onClick={() => setTab("meta")} className={`rounded-full px-4 py-1.5 text-xs font-medium transition-all ${tab === "meta" ? "bg-card text-white shadow" : "text-muted-foreground"}`}>Dettagli</button>
          </div>
          <div className="surface mt-4 max-h-[520px] overflow-y-auto p-5">
            {tab === "sorgente" ? (
              sourceText ? (
                <p data-testid="source-text" className="whitespace-pre-wrap text-sm leading-relaxed text-gray-300">{sourceText}</p>
              ) : (
                <p data-testid="source-text-pending" className="text-sm text-muted-foreground">
                  {isProcessing(c.stato) ? "In attesa di trascrizione / estrazione del testo…" : "Nessun testo disponibile."}
                </p>
              )
            ) : (
              <dl data-testid="content-meta" className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-xs">
                {[["ID", c.id], ["Tipo", c.tipo], ["URL", c.source_url || "—"], ["Kind", c.file_kind || "—"], ["Hash", c.content_hash.slice(0, 20) + "…"], ["MIME", c.mime_type || "—"], ["Storage", c.storage_path || "—"], ["Creato", formatDate(c.created_at)], ["Aggiornato", formatDate(c.updated_at)]].map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className="break-all font-mono">{v}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </section>

        <section className="fade-up min-w-0">
          <h2 className="text-base font-semibold md:text-lg">Conoscenze estratte <span className="font-mono text-muted-foreground">({c.knowledge.length})</span></h2>
          <div className="mt-4 space-y-3">
            {c.knowledge.length === 0 ? (
              <div data-testid="knowledge-empty-detail" className="surface p-8 text-center text-sm text-muted-foreground">
                {isProcessing(c.stato) ? "Estrazione in corso…" : "Nessuna conoscenza estratta da questo contenuto."}
              </div>
            ) : (
              c.knowledge.map((k) => (
                <div key={k.id} data-testid={`detail-knowledge-${k.id}`} className="surface surface-hover group p-4">
                  <div className="flex items-start justify-between gap-3">
                    <CategoryBadge categoria={k.categoria} />
                    <button data-testid={`detail-knowledge-edit-${k.id}`} onClick={() => setEditing(k)} className="rounded-md p-1.5 text-muted-foreground transition-opacity hover:bg-secondary hover:text-white sm:opacity-0 sm:group-hover:opacity-100">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed">{k.testo}</p>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title="Eliminare questo contenuto?"
        description="Verranno eliminate anche tutte le conoscenze estratte da questo contenuto."
        onCancel={() => setConfirmDelete(false)}
        onConfirm={remove}
      />
      <KnowledgeEditDialog item={editing} intents={intents} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
